import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { UtilityService } from 'src/app/modules/accounts-module/accounts/services/utility-service';
import { CustomerModel } from '../../models/customer-model';
import { CustomerMasterService } from '../../services/customer-master-service';
import { TransactionMasterService } from '../../services/transaction-master-service';
import {
  TransactionDto,
  TransactionListFilterPayload,
} from '../../models/transaction-list-model';
import { TransactionCreatePayload } from '../../models/transaction-create-model';
import { TransactionStatus } from '../../models/transaction.enum';
import { TransactionType } from '../../models/transaction.enum';
import { MatDialog } from '@angular/material/dialog';
import { TransactionMetadataPopupComponent } from '../transaction-metadata-popup/transaction-metadata-popup.component';
import { TransactionQuotationPopupComponent } from '../transaction-quotation-popup/transaction-quotation-popup.component';
import { QuotationMetaDataModel } from '../../models/transaction-quotation-model';
import { MetaDataModel } from '../../models/transaction-metadata-model';

@Component({
  selector: 'app-transaction-list',
  templateUrl: './transaction-list.component.html',
  styleUrls: ['./transaction-list.component.css'],
})
export class TransactionListComponent implements OnInit {
  public TransactionStatus = TransactionStatus;
  public TransactionType = TransactionType;

  //This is test comment to be removed
  // Filters
  filterTransactionType: TransactionType = TransactionType.QUOTATION;
  filterCustomerId: number | null = null;
  filterFromDate: Date | null = null;
  filterToDate: Date | null = null;

  // Frontend Filters
  filterIsActive: string = 'All';
  filterStatus: string = 'All';

  // Dropdown Data
  transactionTypes = [
    { value: TransactionType.QUOTATION, viewValue: 'Quotation' },
    { value: TransactionType.DIRECT_ORDER, viewValue: 'Direct Order' },
    { value: TransactionType.ORDER, viewValue: 'Order' },
    { value: TransactionType.INVOICE, viewValue: 'Invoice' },
  ];
  customerList: CustomerModel[] = [];
  statusOptions = [
    { value: TransactionStatus.PENDING, viewValue: 'Pending' },
    { value: TransactionStatus.INPROCESS, viewValue: 'In Process' },
    { value: TransactionStatus.COMPLETED, viewValue: 'Completed' },
    { value: TransactionStatus.CANCELLED_TRANSACTION, viewValue: 'Cancelled' },
  ];

  // Table
  displayedColumns: string[] = [
    'srNo',
    'parentTransactionNo',
    'transactionNo',
    'customerName',
    'createdOn',
    'isGstTransaction',
    'status',
    'isActive',
    'actions',
  ];
  dataSource = new MatTableDataSource<TransactionDto>([]);
  originalData: TransactionDto[] = []; // Store original API data for frontend filtering

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private _router: Router,
    private _utilityService: UtilityService,
    private _customerService: CustomerMasterService,
    private _transactionService: TransactionMasterService,
    private _dialog: MatDialog
  ) { }

  async ngOnInit(): Promise<void> {
    await this.lFN_LoadCustomers();
    this.lFN_SearchTransactions(); // Initial load
  }

  isQuotation() {
    return this.filterTransactionType === TransactionType.QUOTATION;
  }

  isOrder() {
    return (
      this.filterTransactionType === TransactionType.ORDER ||
      this.filterTransactionType === TransactionType.DIRECT_ORDER
    );
  }
  isInvoice() {
    return this.filterTransactionType === TransactionType.INVOICE;
  }

  async lFN_LoadCustomers(): Promise<void> {
    this._customerService.lFN_GetCustomerList().subscribe({
      next: (res: any) => {
        const parsed = JSON.parse(res.body);
        this.customerList = parsed?.data || [];
      },
      error: () => console.error('Failed to load customers'),
    });
  }

  lFN_SearchTransactions(): void {
    if (!this.filterTransactionType) {
      this._utilityService.lFN_ErrorToaster(
        'Transaction Type is mandatory',
        'Warning'
      );
      return;
    }

    this._utilityService.lFN_ShowLoader();

    // Dynamically build payload to send only what is selected
    const payload: Partial<TransactionListFilterPayload> = {
      transactionType: this.filterTransactionType,
    };

    if (this.filterCustomerId) payload.customerId = this.filterCustomerId;
    if (this.filterFromDate)
      payload.fromDate = new Date(this.filterFromDate).toISOString();
    if (this.filterToDate)
      payload.toDate = new Date(this.filterToDate).toISOString();

    if (this.filterIsActive && this.filterIsActive !== 'All') {
      payload.isActive = this.filterIsActive === 'Yes' ? 'YES' : 'NO';
    }

    if (this.filterStatus && this.filterStatus !== 'All') {
      // If your API expects codes like 'P' instead of 'Pending', map them here. Assuming literal matches:
      payload.status = this.filterStatus;
    }

    this._transactionService.lFN_GetTransactionListFilter(payload).subscribe({
      next: (res: any) => {
        this._utilityService.lFN_HideLoader();
        const parsed = JSON.parse(res.body);
        this.originalData = parsed?.data || [];
        this.applyFrontendFilters();
      },
      error: () => {
        this._utilityService.lFN_HideLoader();
        this._utilityService.lFN_ErrorToaster(
          'Failed to load transactions',
          'Error'
        );
        this.dataSource.data = [];
        this.originalData = [];
      },
    });
  }

  applyFrontendFilters(): void {
    let filtered = [...this.originalData];

    // Filter Is Active
    if (this.filterIsActive !== 'All') {
      // Since the response model doesn't explicitly expose 'isActive', adapt as needed.
      // Typically frontend filter would act on `isGstTransaction` or similar if `isActive` was missing.
      // If the API strictly handles this, the frontend filter might be redundant.
    }

    // Filter Status
    if (this.filterStatus !== 'All') {
      filtered = filtered.filter(
        (x) => x.transactionStatus === this.filterStatus
      );
    }

    this.dataSource.data = filtered;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  lFN_ResetFilters(): void {
    this.filterTransactionType = TransactionType.QUOTATION;
    this.filterCustomerId = null;
    this.filterFromDate = null;
    this.filterToDate = null;
    this.filterIsActive = 'All';
    this.filterStatus = 'All';
    this.lFN_SearchTransactions();
  }

  getStatusName(statusValue: string): string {
    switch (statusValue) {
      case TransactionStatus.PENDING:
        return 'Pending';
      case TransactionStatus.INPROCESS:
        return 'In Process';
      case TransactionStatus.COMPLETED:
        return 'Completed';
      case TransactionStatus.CANCELLED_TRANSACTION:
        return 'Cancelled';
      default:
        return statusValue;
    }
  }

  onTypeChange(): void {
    this.lFN_SearchTransactions();
  }

  // Actions
  lFN_Edit(row: any): void {
    if (
      row.transactionType === TransactionType.QUOTATION &&
      row.transactionStatus === TransactionStatus.INPROCESS
    ) {
      this._utilityService.lFN_WarningToaster(
        'In process quotations cannot be edited.',
        'Action Denied'
      );
      return;
    }

    if (
      row.transactionType === TransactionType.DIRECT_ORDER &&
      row.transactionStatus === TransactionStatus.COMPLETED
    ) {
      this._utilityService.lFN_WarningToaster(
        'Completed direct orders cannot be edited.',
        'Action Denied'
      );
      return;
    }

    if (
      row.transactionType === TransactionType.QUOTATION &&
      row.transactionStatus === TransactionStatus.COMPLETED
    ) {
      this._utilityService.lFN_WarningToaster(
        'Completed quotations cannot be edited.',
        'Action Denied'
      );
      return;
    }

    if (
      row.transactionType === TransactionType.ORDER
    ) {
      this._utilityService.lFN_WarningToaster(
        'Orders cannot be edited.',
        'Action Denied'
      );
      return;
    }

    if (
      row.transactionType === TransactionType.INVOICE
    ) {
      this._utilityService.lFN_WarningToaster(
        'Invoices cannot be edited.',
        'Action Denied'
      );
      return;
    }



    this._utilityService.lFN_NavigateViaExtras(
      'customertransaction',
      { id: row.transactionId },
      'edit'
    );
  }

  lFN_View(row: any): void {
    this._utilityService.lFN_NavigateViaExtras(
      'customertransaction',
      { id: row.transactionId },
      'view'
    );
  }

  lFN_ToggleActive(row: any): void {
    // Validation: Cancelled orders cannot be made active/inactive
    if (
      (row.transactionType === TransactionType.ORDER ||
        row.transactionType === TransactionType.DIRECT_ORDER) &&
      row.transactionStatus === TransactionStatus.CANCELLED_TRANSACTION
    ) {
      this._utilityService.lFN_WarningToaster(
        'Cannot change the Status of Cancelled order',
        'Action Denied'
      );
      return;
    }

    // Validation: Invoice cannot be made active/inactive
    if (row.transactionType === TransactionType.INVOICE) {
      this._utilityService.lFN_WarningToaster(
        'Invoices cannot be made active or inactive.',
        'Action Denied'
      );
      return;
    }

    // Validation: In process quotation cannot be converted to order
    if (
      row.transactionType === TransactionType.QUOTATION &&
      row.transactionStatus === TransactionStatus.INPROCESS
    ) {
      this._utilityService.lFN_WarningToaster(
        'In process quotations cannot be converted to an order.',
        'Action Denied'
      );
      return;
    }

    // Validation: Quotations and Orders can only be toggled if their status is PENDING
    if (
      (row.transactionType === TransactionType.QUOTATION ||
        row.transactionType === TransactionType.ORDER ||
        row.transactionType === TransactionType.DIRECT_ORDER) &&
      row.transactionStatus !== TransactionStatus.PENDING
    ) {
      this._utilityService.lFN_WarningToaster(
        'Only pending transactions can be made active or inactive.',
        'Action Denied'
      );
      return;
    }

    const isCurrentlyActive = row.isActive === 'YES';
    const newStatus = isCurrentlyActive ? 'In Active' : 'Active';
    const l_activeStatus: string = isCurrentlyActive ? 'NO' : 'YES';

    const dialogData = {
      title: `Change Status - ${row.transactionNo}`,
      message: `Are you sure you want to change status to ${newStatus} for ${row.transactionNo}?`,
      buttons: [
        {
          label: 'Yes',
          value: true,
          color: 'primary',
          className: 'btn-yes-custom',
        },
        { label: 'No', value: false, color: 'warn' },
      ],
    };

    this._utilityService
      .lFN_ShowConfirmationDialog(
        dialogData.title,
        dialogData.message,
        dialogData.buttons
      )
      .subscribe((confirmed) => {
        if (!confirmed) {
          // Revert toggle if cancelled (needs logic to reload or reset control)
          this.applyFrontendFilters(); // Simple refresh
          return;
        }

        this._utilityService.lFN_ShowLoader();
        this._transactionService
          .lFN_ToggleActive(row.transactionId, l_activeStatus)
          .subscribe({
            next: (res: any) => {
              this._utilityService.lFN_HideLoader();
              this._utilityService.lFN_SuccessToaster(
                'Transaction status updated successfully',
                'Success'
              );
              this.lFN_SearchTransactions(); // Refresh the list
            },
            error: (err: any) => {
              this._utilityService.lFN_HideLoader();
              const errorMsg =
                err.error?.message || 'Failed to update transaction status';
              this._utilityService.lFN_ErrorToaster(errorMsg, 'Error');
              this.applyFrontendFilters(); // Revert toggle visually
            },
          });
      });
  }

  lFN_CancelOrder(row: any): void {
    // Validation: Cancel is only applicable to Orders and Direct Orders
    if (
      row.transactionType !== TransactionType.ORDER &&
      row.transactionType !== TransactionType.DIRECT_ORDER
    ) {
      this._utilityService.lFN_WarningToaster(
        'Only orders and direct orders can be cancelled.',
        'Action Denied'
      );
      return;
    }

    const dialogData = {
      title: `Cancel Order - ${row.transactionNo}`,
      message: `Are you sure you want to cancel order ${row.transactionNo}?`,
      buttons: [
        { label: 'Yes', value: true, color: 'primary', className: 'btn-yes-custom' },
        { label: 'No', value: false, color: 'warn' },
      ],
    };

    this._utilityService
      .lFN_ShowConfirmationDialog(dialogData.title, dialogData.message, dialogData.buttons)
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this._utilityService.lFN_ShowLoader();
        this._transactionService.lFN_CancelTransactionById(row.transactionId).subscribe({
          next: (res: any) => {
            this._utilityService.lFN_HideLoader();
            this._utilityService.lFN_SuccessToaster('Order cancelled successfully', 'Success');
            this.lFN_SearchTransactions(); // Refresh the list
          },
          error: (err: any) => {
            this._utilityService.lFN_HideLoader();
            const errorMsg = err.error?.message || 'Failed to cancel order.';
            this._utilityService.lFN_ErrorToaster(errorMsg, 'Error');
          },
        });
      });
  }

  lFN_ConvertToOrder(row: TransactionDto): void {
    // Validation: Inactive quotation cannot be converted to order
    if ((row as any).isActive === 'NO') {
      this._utilityService.lFN_WarningToaster(
        'Inactive quotations cannot be converted to an order.',
        'Action Denied'
      );
      return;
    }

    // Validation: Completed quotation means order is already generated
    if (row.transactionStatus === TransactionStatus.COMPLETED || row.transactionStatus === TransactionStatus.INPROCESS) {
      this._utilityService.lFN_WarningToaster(
        'Order already generated.',
        'Warning'
      );
      return;
    }

    const dialogData = {
      title: `Convert to Order - ${row.transactionNo}`,
      message: `Are you sure you want to convert quotation ${row.transactionNo} to an order?`,
      buttons: [
        {
          label: 'Yes',
          value: true,
          color: 'primary',
          className: 'btn-yes-custom',
        },
        { label: 'No', value: false, color: 'warn' },
      ],
    };

    this._utilityService
      .lFN_ShowConfirmationDialog(
        dialogData.title,
        dialogData.message,
        dialogData.buttons
      )
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this._utilityService.lFN_ShowLoader();

        // Construct payload using helper to ensure correct tax and total calculations
        const payload: TransactionCreatePayload = this.buildTransactionPayload(row, TransactionType.ORDER);

        this._transactionService.lFN_SaveTransaction(payload).subscribe({
          next: (res: any) => {
            this._utilityService.lFN_HideLoader();
            this._utilityService.lFN_SuccessToaster(
              'Order created successfully',
              'Success'
            );
            this.lFN_SearchTransactions(); // Refresh the list to reflect the new order
          },
          error: (err: any) => {
            this._utilityService.lFN_HideLoader();
            this._utilityService.lFN_ErrorToaster(
              'Failed to convert quotation to order.',
              'Error'
            );
          },
        });
      });
  }

  lFN_ConvertToInvoice(row: any): void {
    // Validation: Inactive order or direct order cannot be converted to invoice
    if (row.isActive === 'NO') {
      this._utilityService.lFN_WarningToaster(
        'Inactive orders cannot be converted to an invoice.',
        'Action Denied'
      );
      return;
    }

    if (row.transactionStatus === TransactionStatus.CANCELLED_TRANSACTION) {
      this._utilityService.lFN_WarningToaster(
        'Cancelled orders cannot be converted to an invoice.',
        'Action Denied'
      );
      return;
    }

    // Validation: Completed order means invoice is already generated
    if (row.transactionStatus === TransactionStatus.COMPLETED) {
      this._utilityService.lFN_WarningToaster(
        'Invoice already generated.',
        'Warning'
      );
      return;
    }

    const dialogData = {
      title: `Convert to Invoice - ${row.transactionNo}`,
      message: `Are you sure you want to convert order ${row.transactionNo} to an invoice?`,
      buttons: [
        {
          label: 'Yes',
          value: true,
          color: 'primary',
          className: 'btn-yes-custom',
        },
        { label: 'No', value: false, color: 'warn' },
      ],
    };

    this._utilityService
      .lFN_ShowConfirmationDialog(
        dialogData.title,
        dialogData.message,
        dialogData.buttons
      )
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this._utilityService.lFN_ShowLoader();

        const payload: TransactionCreatePayload = this.buildTransactionPayload(row, TransactionType.INVOICE);

        this._transactionService.lFN_SaveTransaction(payload).subscribe({
          next: (res: any) => {
            this._utilityService.lFN_HideLoader();
            this._utilityService.lFN_SuccessToaster(
              'Invoice created successfully',
              'Success'
            );
            this.lFN_SearchTransactions(); // Refresh list to reflect changes
          },
          error: (err: any) => {
            this._utilityService.lFN_HideLoader();
            this._utilityService.lFN_ErrorToaster(
              err?.error?.message || 'Failed to generate invoice',
              'Error'
            );
          },
        });
      });
  }

  private buildTransactionPayload(row: any, targetType: string): TransactionCreatePayload {
    const details = (row.transactionDetails || []).map((detail: any) => {
      const qty = detail.quantity || 1;
      const rate = detail.rate || 0;
      const gross = qty * rate;
      let discountAmount = detail.discountAmount || 0;
      if (!discountAmount && detail.discountPercent && gross > 0) {
        discountAmount = Number(((gross * detail.discountPercent) / 100).toFixed(2));
      }
      const amount = detail.amount || (gross - discountAmount > 0 ? gross - discountAmount : 0);
      const discountPercent = gross > 0 ? Number(((discountAmount / gross) * 100).toFixed(2)) : 0;

      return {
        ...(detail as any),
        transactionDetailId: null,
        serviceHeaderId: detail.serviceId,
        quantity: qty,
        rate: rate,
        discountAmount: discountAmount,
        discountPercent: discountPercent,
        amount: amount,
        workUnits: detail.workUnits ? String(detail.workUnits) : null,
        scheduleCustomSmsList: [],
        serviceWorkDescription: detail.serviceWorkDescription || '',
      };
    });

    const subTotal = details.reduce((acc: number, d: any) => acc + (d.quantity * d.rate), 0);
    const totalDiscount = details.reduce((acc: number, d: any) => acc + (d.discountAmount || 0), 0) || (row.totalDiscount || 0);
    const taxableAmount = Math.max(subTotal - totalDiscount, 0);
    const isGst = row.isGstTransaction === 'YES';
    const igstPercent = row.igstPercent || 0;
    const cgstPercent = row.cgstPercent || 0;
    const sgstPercent = row.sgstPercent || 0;
    const igstAmount = isGst ? (taxableAmount * igstPercent) / 100 : 0;
    const cgstAmount = isGst ? (taxableAmount * cgstPercent) / 100 : 0;
    const sgstAmount = isGst ? (taxableAmount * sgstPercent) / 100 : 0;
    const totalGst = igstAmount + cgstAmount + sgstAmount;
    const totalAmount = taxableAmount + totalGst;
    const totalDiscountPercent = subTotal > 0 ? Number(((totalDiscount / subTotal) * 100).toFixed(2)) : 0;

    return {
      ...(row as any),
      transactionId: null,
      transactionNo: null,
      transactionType: targetType,
      parentTransactionId: row.transactionId,
      isGstTransaction: isGst ? 'YES' : 'NO',
      igstPercent: igstPercent,
      igstAmount: igstAmount,
      cgstPercent: cgstPercent,
      cgstAmount: cgstAmount,
      sgstPercent: sgstPercent,
      sgstAmount: sgstAmount,
      totalDiscountPercent: totalDiscountPercent,
      totalDiscount: totalDiscount,
      totalAmount: totalAmount,
      metadataJson: '',
      transactionDetails: details,
    };
  }

  lFN_PrintInvoice(row: any): void {
    row.isPrinting = true; // Disable the print button
    let existingMetadataObj = null;
    if (row.hasMetadata && row.transactionMetadata) {
      try {
        existingMetadataObj = JSON.parse(row.transactionMetadata);
      } catch (e) {
        console.error('Failed to parse existing transaction metadata', e);
      }
    }

    // Open metadata popup
    const dialogRef = this._dialog.open(TransactionMetadataPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        transactionId: row.transactionId,
        transactionNo: row.transactionNo,
        existingMetadata: existingMetadataObj
      },
    });

    dialogRef.afterClosed().subscribe((result: MetaDataModel) => {
      row.isPrinting = false; // Re-enable the print button when the dialog closes
      if (result) {
        this._utilityService.lFN_ShowLoader();
        this._transactionService
          .lFN_GenerateInvoice(row.transactionId, result)
          .subscribe({
            next: (res: any) => {
              this._utilityService.lFN_HideLoader();
              if (res.body && res.body.size > 0) {
                const blob = new Blob([res.body], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `invoice-${row.transactionNo || row.transactionId
                  }.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                this._utilityService.lFN_SuccessToaster(
                  'Invoice downloaded successfully.',
                  'Success'
                );
                this.lFN_SearchTransactions();
              } else {
                this._utilityService.lFN_ErrorToaster(
                  'Failed to download invoice: Empty file received.',
                  'Error'
                );
              }
            },
            error: (err: any) => {
              this._utilityService.lFN_HideLoader();
              if (
                err.error instanceof Blob &&
                err.error.type === 'application/json'
              ) {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                  const errorData = JSON.parse(e.target.result);
                  this._utilityService.lFN_ErrorToaster(
                    errorData.message ||
                    'An error occurred while generating the invoice.',
                    'Error'
                  );
                };
                reader.readAsText(err.error);
              } else {
                this._utilityService.lFN_ErrorToaster(
                  'An error occurred while generating the invoice.',
                  'Error'
                );
              }
            },
          });
      }
    });
  }

  lFN_PrintQuotation(row: any): void {
    row.isPrinting = true; // Disable the print button
    let existingMetadataObj = null;
    if (row.hasMetadata && row.transactionMetadata) {
      try {
        existingMetadataObj = JSON.parse(row.transactionMetadata);
      } catch (e) {
        console.error('Failed to parse existing transaction metadata', e);
      }
    }

    // Open quotation metadata popup
    const dialogRef = this._dialog.open(TransactionQuotationPopupComponent, {
      width: '600px',
      disableClose: true,
      data: {
        transactionId: row.transactionId,
        transactionNo: row.transactionNo,
        metadata: existingMetadataObj
      },
    });

    dialogRef.afterClosed().subscribe((result: QuotationMetaDataModel) => {
      row.isPrinting = false; // Re-enable the print button when the dialog closes
      if (result) {
        this._utilityService.lFN_ShowLoader();
        this._transactionService
          .lFN_GenerateQuotation(row.transactionId, result)
          .subscribe({
            next: (res: any) => {
              this._utilityService.lFN_HideLoader();
              if (res.body && res.body.size > 0) {
                const blob = new Blob([res.body], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `quotation-${row.transactionNo || row.transactionId
                  }.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                this._utilityService.lFN_SuccessToaster(
                  'Quotation downloaded successfully.',
                  'Success'
                );
                this.lFN_SearchTransactions();
              } else {
                this._utilityService.lFN_ErrorToaster(
                  'Failed to download quotation: Empty file received.',
                  'Error'
                );
              }
            },
            error: (err: any) => {
              this._utilityService.lFN_HideLoader();
              if (err.error instanceof Blob && err.error.type === 'application/json') {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                  const errorData = JSON.parse(e.target.result);
                  this._utilityService.lFN_ErrorToaster(
                    errorData.message || 'An error occurred while generating the quotation.',
                    'Error'
                  );
                };
                reader.readAsText(err.error);
              } else {
                this._utilityService.lFN_ErrorToaster('An error occurred while generating the quotation.', 'Error');
              }
            },
          });
      }
    });
  }
}
